/**
 * PKCE (Proof Key for Code Exchange) Utilities
 *
 * RFC 7636 compliant implementation for OAuth 2.0 PKCE flow.
 * code_verifier is generated client-side and never sent to backend
 * until the token exchange step.
 *
 * @see https://datatracker.ietf.org/doc/html/rfc7636
 */

/**
 * Generate a cryptographically random code_verifier for PKCE.
 *
 * Per RFC 7636 Section 4.1:
 * - Must be between 43 and 128 characters
 * - Must use unreserved characters: [A-Z] / [a-z] / [0-9] / "-" / "." / "_" / "~"
 * - We use 64 random bytes (86 characters after base64url encoding)
 *
 * @returns Base64url-encoded random string (86 characters)
 */
export function generateCodeVerifier(): string {
  // 64 bytes = 512 bits of entropy, encodes to 86 base64url characters
  const randomBytes = new Uint8Array(64)
  crypto.getRandomValues(randomBytes)
  return base64UrlEncode(randomBytes)
}

/**
 * Generate code_challenge from code_verifier using SHA-256.
 *
 * Per RFC 7636 Section 4.2:
 * code_challenge = BASE64URL(SHA256(ASCII(code_verifier)))
 *
 * @param codeVerifier - The code_verifier string
 * @returns Promise resolving to base64url-encoded SHA-256 hash
 */
export async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  // Convert verifier to bytes
  const encoder = new TextEncoder()
  const data = encoder.encode(codeVerifier)

  // Compute SHA-256 hash
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)

  // Convert to base64url
  return base64UrlEncode(new Uint8Array(hashBuffer))
}

/**
 * Base64url encode a Uint8Array.
 *
 * Per RFC 4648 Section 5:
 * - Use '-' instead of '+'
 * - Use '_' instead of '/'
 * - Remove padding '='
 *
 * @param bytes - Uint8Array to encode
 * @returns Base64url-encoded string without padding
 */
function base64UrlEncode(bytes: Uint8Array): string {
  // Convert to base64
  const base64 = btoa(String.fromCharCode(...bytes))

  // Convert to base64url (URL-safe variant)
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

/**
 * Generate both code_verifier and code_challenge for PKCE flow.
 *
 * Convenience function that generates both values in one call.
 *
 * @returns Promise resolving to object with codeVerifier and codeChallenge
 *
 * @example
 * ```typescript
 * const { codeVerifier, codeChallenge } = await generatePKCEPair()
 *
 * // Store verifier in sessionStorage
 * sessionStorage.setItem('oauth_code_verifier', codeVerifier)
 *
 * // Send challenge to backend with initiate request
 * const response = await connectionService.initiateOAuth({
 *   connectionUid: 'xxx',
 *   redirectUri: 'https://...',
 *   codeChallenge: codeChallenge
 * })
 * ```
 */
export async function generatePKCEPair(): Promise<{
  codeVerifier: string
  codeChallenge: string
}> {
  const codeVerifier = generateCodeVerifier()
  const codeChallenge = await generateCodeChallenge(codeVerifier)

  return { codeVerifier, codeChallenge }
}
